
import * as React from 'react';
import { createRef, useEffect, useRef, useState } from 'react';
import ProfileNavbar, { NavbarItemProps } from "../../components/profileNavbar";
import avatar from "../../assets/images/avatar.jpg"
import { Roles } from '../../components/roles';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../components/ui/Dialog';
import Button from '../../components/ui/Button';
import { Check, X } from 'lucide-react';
import { connect, useSelector } from 'react-redux';
import Cropper from 'react-easy-crop';
import { getUser, getUserManagers, updateProfileImage } from '../../api/General/account';
import { useTranslation } from "react-i18next";
import { setSelectedUser, setSelectedUserManagers, setImageUrl, setUserFaceImage } from '../../store/redux/actions/userActionCreator';
import getCroppedImg from '../../utils/cropImage';
import { ProfileImageInput } from '../../models/general';
import { getFullUrl } from '../../utils/url';
import { useProfileTabConfig } from '../../hooks/useProfileTabConfig';
import { useFileUploadLimits } from '../../hooks/useFileUploadLimits';
import useCanManageAssets from '../../hooks/useCanManageAssets';
import ResetPasswordDialog from '../EmployeesCycle/General/ResetPasswordDialog';
import AppIcon from '../../components/AppIcon';
import AccessDenied from '../../components/AccessDenied';
import { ErrorState } from '../../components/AsyncStates';
import { getMyMfaStatus } from '../../api/General/Mfa';


type Props = {
  profile?: boolean,
  children?: any,
  prevUrl?: any,
  userId?:string,
  role?: any
  imageUrl?: string
  selectedUser?: any
  selectedUserManagers?: any
  setSelectedUser?: any
  setSelectedUserManagers?: any
  setImageUrl?: (url: string) => void
  setUserFaceImage?: (img: any) => void
  companyPrimaryColor?: string
}

const UserProfile =  (props: Props) => {
  const { t } = useTranslation('common');
  const fileInputRef = createRef<HTMLInputElement>();

  const baseUrl = window.location.origin;
  const userId = props.userId
  const { id } = useParams()
  const previousLocation = props.prevUrl
  const splittedUrl = previousLocation?.split(baseUrl) != undefined ? previousLocation?.split(baseUrl) : baseUrl + "/apps"
  const url = splittedUrl.length == 1 ? splittedUrl[0] : splittedUrl[1]
  const companyName = "Numow"
  const logo = avatar

  /**
   * CO-265 — a refused profile must reach a TERMINAL state.
   *
   * `loading` below is derived purely from Redux, so before this state existed a
   * rejected `getUser` (cross-tenant 401, deleted employee 404, a 5xx) simply never
   * populated `selectedUser` and the shell spun forever. Every one of the ~23
   * `/profile/:id/...` sub-pages renders through this shell, so all of them shared
   * the defect — and an isolation assertion of the form "the other company's data is
   * not on screen" passes vacuously against a page that never finishes loading.
   */
  type LoadFailure = 'denied' | 'notFound' | 'error';
  /**
   * CO-266 — the refusal is stored WITH the profile it was recorded for, and the mismatch is
   * resolved during RENDER.
   *
   * `setLoadFailure(null)` lives at the top of `fetchData`, which runs from a useEffect — a
   * passive effect React flushes AFTER paint. So on the first render following an `id` change
   * the component still held the previous profile's `'denied'` and painted "This employee
   * profile isn't available to you" FOR THE NEW EMPLOYEE for one frame before clearing. The end
   * state was right, but that frame is the same false accusation this whole state exists to
   * remove, and on a slow device it is long enough to read.
   *
   * Deriving it here removes the frame entirely: a failure stamped for profile A is simply not
   * a failure for profile B, no effect required. `fetchData` still clears it, which keeps a
   * RETRY of the same id working.
   */
  const [loadFailure, setLoadFailure] = useState<{ forId: string | undefined; failure: LoadFailure } | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  // Identifies the profile whose fetch is allowed to write state; see fetchData.
  const activeRequestRef = useRef<string | undefined>(undefined);

  // The profile this render is about — the same value fetchData stamps its refusal with.
  const profileId = id != undefined ? id : (userId as string);
  const activeFailure: LoadFailure | null =
    loadFailure != null && loadFailure.forId === profileId ? loadFailure.failure : null;

  const loading = activeFailure == null && (props.selectedUser == undefined || props.selectedUser.id != id)

  const isViewingAnotherUser = id != undefined && id != userId;
  const isHr = Roles.HR.includes(props.role);
  const isManagerOfEmployee = (props.selectedUserManagers ?? []).some((s: any) => String(s?.id) === String(userId));

  const isAdmin = Roles.ADMIN.includes(props.role);
  const canAccessSkillsTab = !isViewingAnotherUser || !isAdmin;
  const canAccessPayrollTab = !isViewingAnotherUser || isHr;
  const canAccessAttendanceTab = !isViewingAnotherUser || isHr || isManagerOfEmployee;
  const hideDocumentsTab = isAdmin && isViewingAnotherUser;
  // ASSETS tab visibility mirrors the route guard (ASSETS_PAGE_ROLES = Hr OR the
  // AssetManagement tag) and the server's `IsAssetManager`. Admin is NOT implicitly an
  // asset manager and Operator is outside the asset model entirely, so showing them the
  // tab on someone else's profile advertised access the server correctly refuses — the
  // click only bounced them back to the dashboard. Own profile stays visible to every
  // company role (an employee reads the assets assigned to them).
  const isOperator = Roles.OPERATOR.includes(props.role);
  const canManageAssets = useCanManageAssets();
  const canAccessAssetsTab = !isOperator && (!isViewingAnotherUser || canManageAssets);

  /*
    Profile tab module config (operator-controlled per company).

    🔴 `loaded` is not optional here. `isTabEnabled` FAILS OPEN while the config is in flight — it
    answers `true` for every tab — so rendering the strip before `loaded` paints the full tab list
    and then drops the operator-disabled ones a tick later. That flash was visible on EVERY tab
    switch, because each `/profile/:id/<tab>` is its own route rendering its own `<UserProfile>`,
    so this shell remounts per tab and the hook restarted from "not loaded" each time.

    The hook now seeds synchronously from the shared nav-config cache, so in practice `loaded` is
    already true on the first paint and nothing is withheld. The gate below is what makes that a
    guarantee rather than a timing accident.
  */
  const { isTabEnabled, loaded: tabConfigLoaded } = useProfileTabConfig();
  const { validateFileSize, getMaxSizeMB } = useFileUploadLimits();

  /*
    User can edit their own image, or HR/Admin can edit others.

    CO-692 — EXCEPT on an ARCHIVED employee. HR can now open an archived leaver's profile (the
    account read used to 404, which is what kept this unreachable), and the same "history is
    read-only" contract that hides Edit on the Personal tab applies to the avatar: the server's
    image write resolves the subject through the non-archived user set and answers NotFound, so the
    camera overlay could only lead to a silent failure. Withheld rather than disabled — the Archived
    chip in the header already carries the reason.
  */
  const isArchivedSubject = Boolean(props.selectedUser?.isArchived)
    || props.selectedUser?.lifecycleState === 'Archived';
  const canEditImage = (!isViewingAnotherUser || isHr || isAdmin) && !isArchivedSubject;

  // Image upload state
  const [showImagePreviewDialog, setShowImagePreviewDialog] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  // CO-323 — the "Two-factor authentication" entry is shown only once the server confirms the platform
  // master switch (Mfa:Enabled) is on. Hidden while loading and on any failure, so it never flashes in
  // and then disappears (owner decision 2026-09-15: MFA hidden on prod for 24 h after v1.0.56).
  const [mfaPlatformEnabled, setMfaPlatformEnabled] = useState(false);
  const isOwnProfile = userId == id;
  useEffect(() => {
    if (!isOwnProfile) return;
    let cancelled = false;
    getMyMfaStatus()
      .then((res) => { if (!cancelled) setMfaPlatformEnabled(res?.payload?.platformEnabled === true); })
      .catch(() => { if (!cancelled) setMfaPlatformEnabled(false); });
    return () => { cancelled = true; };
  }, [isOwnProfile]);

  // Operator deliberately excluded — resetting another employee's credential is company employee
  // data (CLAUDE.md Role Model), and the API now 403s for Operator.
  // CO-722 (owner decision 2026-09-15) — an Admin who is not HR may not reset the password of an HR,
  // Admin or Operator user. The server (AccountService.AdminBlockedFromPrivilegedTargetAsync) is the real
  // gate; this only withholds the action so the Admin is not walked into a 403.
  const targetRoles: string[] = [
    ...((props.selectedUser?.roles as string[] | undefined) ?? []),
    props.selectedUser?.role ?? '',
  ];
  const targetIsPrivileged = targetRoles.some((r) => ['Hr', 'Admin', 'Operator'].includes(r));
  const canResetTargetPassword = isViewingAnotherUser && (isHr || (isAdmin && !targetIsPrivileged));

  // Auto-dismiss success toast
  useEffect(() => {
    if (!successSnackbar) return;
    const timer = setTimeout(() => setSuccessSnackbar(false), 3000);
    return () => clearTimeout(timer);
  }, [successSnackbar]);

  const validateImage = (file: File) => {
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowed.includes(file.type)) { setUploadError(t('Unsupported file type. Please use JPG, PNG, or WEBP.')); return false; }
    const { valid, maxMB } = validateFileSize(file, 'profilePhotoMaxSizeMB');
    if (!valid) { setUploadError(t('fileSizeExceedsLimit', { name: file.name, maxMB })); return false; }
    setUploadError(null);
    return true;
  };

  const handleImageChange = (e: React.FormEvent<HTMLInputElement>) => {
    const f = (e.target as any).files?.[0];
    if (!f) return;
    if (!validateImage(f)) return;
    setPendingFile(f);
    setPreviewImageUrl(URL.createObjectURL(f));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setShowImagePreviewDialog(true);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCancelImage = () => {
    setShowImagePreviewDialog(false);
    setPendingFile(null);
    setPreviewImageUrl(null);
  };

  const handleConfirmImage = async () => {
    if (!pendingFile || !croppedAreaPixels) return;
    try {
      setUploadingImage(true);
      const blob = await getCroppedImg(previewImageUrl!, croppedAreaPixels, 'image/jpeg', 0.92);
      const baseName = pendingFile.name.replace(/\.(png|webp|jpeg|jpg)$/i, '');
      const croppedFile = new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
      const res = await updateProfileImage({ image: croppedFile } as ProfileImageInput, id ?? userId);
      if (res.payload?.imageUrl) {
        // Only update the logged-in user's Redux state when editing own profile
        if (!isViewingAnotherUser) {
          props.setImageUrl?.(res.payload.imageUrl);
          const face = res.payload.userFaceImage ?? { path: res.payload.imageUrl };
          props.setUserFaceImage?.(face);
        }
        // Update selected user in Redux (for the profile page)
        props.setSelectedUser?.({ ...props.selectedUser, imageUrl: res.payload.imageUrl });
        setSuccessSnackbar(true);
      }
    } catch (err) {
      console.error(err);
      setUploadError(t('Failed to update profile image. Please try again.'));
    } finally {
      setShowImagePreviewDialog(false);
      setPendingFile(null);
      setPreviewImageUrl(null);
      setUploadingImage(false);
    }
  };

  const onCropComplete = (_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  /**
   * CO-265 — the fetch MUST re-run on an `id` change, not only on a manual retry.
   *
   * All ~25 `/profile/:id/...` routes render the same element with no `key` (App.tsx),
   * so a client-side navigation from one profile to another changes `id` while keeping
   * this component instance — and every piece of per-profile state with it. With
   * `[reloadNonce]` alone, a `denied`/`notFound` panel raised for profile A stayed on
   * screen after navigating to profile B, telling an authorised HR that a profile they
   * own is "not available to you" — a false accusation, strictly worse than the spinner
   * this state replaced.
   *
   * Keying the route element to force a remount was rejected: it would also discard the
   * image-crop dialog, refetch on every tab hop between profile sub-pages, and leave the
   * SAME stale-state class alive for anyone who later renders this shell without a key.
   * Re-fetching on `id` is the fix at the source.
   */
  useEffect(() => {
      fetchData();
  }, [reloadNonce, id]);

  /**
   * Per-profile transient UI state is scoped to ONE employee; carrying it across an `id`
   * change attributes it to the wrong person (an upload error or "photo updated" toast
   * raised for employee A rendering over employee B, or a reset-password dialog that
   * silently re-targets whoever is now in the URL).
   */
  useEffect(() => {
    setShowImagePreviewDialog(false);
    setPreviewImageUrl(null);
    setPendingFile(null);
    setCroppedAreaPixels(null);
    setUploadError(null);
    setSuccessSnackbar(false);
    setResetPasswordOpen(false);
  }, [id]);

  /**
   * CO-265 — classify the rejection so the screen can say WHICH refusal it was.
   *
   * The status can arrive three ways: `__httpStatus` (stamped by the axios error
   * interceptor, CO-56), a body-level `status` on the `Result<T>` envelope, or
   * `response.status` on a raw AxiosError — the 401 renewal path retries with a bare
   * `axios(originalRequest)` that bypasses the interceptor, so the second refusal
   * comes back in that last shape.
   */
  const failureOf = (err: unknown): LoadFailure => {
    const e = (err ?? {}) as { __httpStatus?: number; status?: number; response?: { status?: number } };
    const status = e.__httpStatus ?? e.response?.status ?? e.status;
    if (status === 401 || status === 403) return 'denied';
    if (status === 404) return 'notFound';
    return 'error';
  };

  const fetchData = async() => {
    // Cleared OUTSIDE the guard on purpose. A refusal recorded for a previous `id` must not
    // survive into this one even when no fetch is needed: after a denied cross-tenant profile,
    // navigating BACK to an employee already cached in redux takes the early-out below, and a
    // clear that lived inside the guard would leave the AccessDenied panel on a profile the
    // user demonstrably owns.
    setLoadFailure(null);
    // Now that the fetch re-runs on `id`, two can be in flight at once (A → B → C clicked
    // quickly). Without this stamp the SLOWER response wins the last write, and both the
    // rendered profile and the refusal panel could belong to an employee no longer in the URL.
    const requestedId = profileId;
    activeRequestRef.current = requestedId;
    const isStale = () => activeRequestRef.current !== requestedId;
    if (props.selectedUser == undefined || props.selectedUser.id != id) {
      let res;
      try {
        res = await getUser(requestedId)
      } catch (err) {
        if (isStale()) return;
        // Stamped with the id it belongs to, so it can never be attributed to a later profile.
        setLoadFailure({ forId: requestedId, failure: failureOf(err) });
        return;
      }
      if (isStale()) return;
      props.setSelectedUser(res.payload)
      // Sync logged-in user's imageUrl from fresh API data when viewing own profile
      if (!isViewingAnotherUser && res.payload?.imageUrl && res.payload.imageUrl !== props.imageUrl) {
        props.setImageUrl?.(res.payload.imageUrl);
      }
      // A managers lookup that fails must not strand the shell: the profile itself
      // already loaded, so degrade to an empty manager list rather than a dead page.
      try {
        const selectedUserManagers = await getUserManagers(requestedId);
        if (isStale()) return;
        props.setSelectedUserManagers(selectedUserManagers.payload);
      } catch {
        if (isStale()) return;
        props.setSelectedUserManagers([]);
      }
    }
  }

  const navBarSideItems: NavbarItemProps[] = canResetTargetPassword
    ? [
        {
          id: "resetPassword",
          content: (
            <span className="inline-flex items-center gap-1.5">
              <AppIcon name="KeyRound" size={14} />
              {t("Reset password")}
            </span>
          ),
          onClick: () => setResetPasswordOpen(true),
        },
      ]
    : [];
  const navBarSideItemsUser = [
    {
      // Direct users to backend Identity UI change password page instead of frontend React page
      id: "changePassword", content: t("CHANGE PASSWORD"), to: "/Account/Manage/ChangePassword"
    },
    // CO-323 — two-factor authentication settings live on the Identity (Razor) pages. Hidden while the
    // platform master switch is off (see mfaPlatformEnabled above).
    ...(mfaPlatformEnabled ? [{
      id: "twoFactorAuthentication", content: t("mfa.profileLink"), to: "/Account/Manage/TwoFactorAuthentication"
    }] : []),
  ]
  const navbarItems: NavbarItemProps[] = [
    {
      id: "personal", content: t("PERSONAL") || "PERSONAL", to: "/profile/" + id + "/personal"
    },
    // The DOCUMENTS tab now also hosts the self-service "On Demand Documents" section
    // (own profile only), so there is no separate My Documents tab.
    ...(!hideDocumentsTab ? [{
      id: "documents", content: t("DOCUMENTS") || "DOCUMENTS", to: "/profile/" + id + "/documents"
    }] : []),
    ...(canAccessSkillsTab ? [{
      id: "skills", content: t("SKILLS") || "SKILLS", to: "/profile/" + id + "/skills"
    }] : []),
    ...(canAccessPayrollTab ? [
      {
        id: "payroll", content: t("PAYROLL") || "PAYROLL", to: "/profile/" + id + "/payroll/active-payroll-month"
      },
    ] : []),
    ...(canAccessAttendanceTab ? [
      ...(isHr ? [{
        id: "attendance", content: t("ATTENDANCE") || "ATTENDANCE", to: "/profile/" + id + "/attendance/rules"
      }] : []),
      {
        id: "leaves", content: t("LEAVES") || "LEAVES", to: "/profile/" + id + "/leaves"
      },
    ] : []),
   ...(Roles.HR.includes(props.role) ? [
    {
      id: "map", content: t("WORKPLACE MAP")||"WORKPLACE MAP", to: "/profile/" + id + "/map"
    },
    ] : []) ,
    ...(canAccessAssetsTab ? [{
      id: "assets", content: t("ASSETS") || "ASSETS", to: "/profile/" + id + "/assets"
    }] : []),
    {
      id: "businessRoles", content: t("BUSINESS ROLES") || "BUSINESS ROLES", to: "/profile/" + id + "/business-roles"
    },
    {
      id: "dependents", content: t("DEPENDENTS") || "DEPENDENTS", to: "/profile/" + id + "/dependents"
    },
  ]

  // Apply module config filtering (additional layer on top of role-based checks).
  // Withheld until the config resolves — see the note on `tabConfigLoaded` above. `undefined`
  // renders the navbar with no tab row rather than with the wrong one.
  const filteredNavbarItems = tabConfigLoaded
    ? navbarItems.filter(item => isTabEnabled(item.id))
    : undefined;

  // CO-265 — terminal states come BEFORE the spinner, so a refused profile can never
  // fall through to an indefinite loader.
  if (activeFailure === 'denied') return (
    <AccessDenied
      title={t('profileAccess.deniedTitle')}
      body={t('profileAccess.deniedBody')}
    />
  );

  if (activeFailure === 'notFound') return (
    <AccessDenied
      iconName="UserX"
      title={t('profileAccess.notFoundTitle')}
      body={t('profileAccess.notFoundBody')}
      showContactHint={false}
    />
  );

  if (activeFailure === 'error') return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <ErrorState
        title={t('profileAccess.errorTitle')}
        message={t('profileAccess.errorBody')}
        retryText={t('profileAccess.retry')}
        onRetry={() => setReloadNonce(n => n + 1)}
      />
    </div>
  );

  if (loading) return (
    // CO-265 — testid'd so the gate spec can assert on THIS spinner. `.animate-spin` is a generic
    // Tailwind class used by unrelated MasterLayout chrome, and a page-wide locator failed the route
    // for any other widget that happened to be spinning at that instant.
    <div className="flex items-center justify-center h-64" data-testid="profile-loading-spinner">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="user-profile-shell">
      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept="image/*"
        capture="user"
        onChange={handleImageChange}
      />

      <ProfileNavbar
        title={companyName ?? "Mowazaf"}
        logo={logo}
        imageUrl={getFullUrl(props.selectedUser?.imageUrl) || undefined}
        items={filteredNavbarItems}
        primaryColor={props.companyPrimaryColor || "var(--mowazaf-primary)"}
        sideItems={userId == id ? navBarSideItemsUser : navBarSideItems}
        fullName={props.selectedUser?.fullName}
        jobTitle={props.selectedUser?.title}
        department={props.selectedUser?.department?.name}
        onImageChange={triggerImageUpload}
        canEditImage={canEditImage}
      />
      <main className="user-profile-content">
        {props.children}
      </main>

      {canResetTargetPassword && id && (
        <ResetPasswordDialog
          open={resetPasswordOpen}
          onOpenChange={setResetPasswordOpen}
          userId={id}
          userDisplayName={props.selectedUser?.fullName || props.selectedUser?.email || id}
        />
      )}

      {/* Image crop dialog */}
      <Dialog open={showImagePreviewDialog} onOpenChange={(open) => { if (!open) handleCancelImage(); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('Crop Profile Picture')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {previewImageUrl && (
              <div className="relative w-full h-[300px] bg-muted rounded-lg">
                <Cropper
                  image={previewImageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
            )}
            <div className="mt-2 px-2">
              <p className="text-sm text-muted-foreground mb-1">{t('Zoom')}</p>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
            </div>
            {uploadError && (
              <p className="text-sm text-destructive mt-1">{uploadError}</p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelImage} disabled={uploadingImage}>{t('Cancel')}</Button>
            <Button
              variant="default"
              onClick={handleConfirmImage}
              disabled={uploadingImage}
              loading={uploadingImage}
            >
              {uploadingImage ? t('Uploading...') : t('Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success toast */}
      {successSnackbar && (
        <div className="fixed top-4 inset-x-0 z-50 flex justify-center pointer-events-none">
          <div role="alert" className="pointer-events-auto flex items-center gap-2 rounded-lg border border-success/20 bg-success/10 px-4 py-3 text-sm text-success shadow-lg animate-in fade-in-0 slide-in-from-top-2 duration-300">
            <Check className="w-4 h-4" />
            <span>{t('Profile picture updated successfully')}</span>
            <button onClick={() => setSuccessSnackbar(false)} aria-label={t('Dismiss')} className="ms-2 p-0.5 rounded hover:bg-success/20 transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );

};

const mapStateToProps = (state: any) => {
  return {
    prevUrl: state.user.prevUrl,
    userId: state.user.userId,
    role: state.user.role,
    imageUrl: state.user.imageUrl,
    selectedUser: state.user.selectedUser,
    selectedUserManagers: state.user.selectedUserManagers,
    companyPrimaryColor: state.user.companyPrimaryColor
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    setSelectedUser: (user: any) => dispatch(setSelectedUser(user)),
    setSelectedUserManagers: (managers: any) => dispatch(setSelectedUserManagers(managers)),
    setImageUrl: (url: string) => dispatch(setImageUrl(url)),
    setUserFaceImage: (img: any) => dispatch(setUserFaceImage(img))
  }
}

export default connect(mapStateToProps,mapDispatchToProps)(UserProfile)

